import { axiosInstance } from '@/lib/axios';
import type {
  BillingBalance,
  Transaction,
  TransactionListRequest,
  TransactionListResponse,
  PaymentRequest,
  PaymentResponse,
  CreditPackage,
  CreditPackagesListResponse,
  PurchaseCreditRequest,
  PurchaseCreditResponse,
  AutoReplenishSettings,
  AutoReplenishUpdateRequest,
} from '@/types/api.types';

/**
 * Billing Service
 * Handles all billing and payment-related API calls
 */

/**
 * Get current user's billing balance with detailed information including trial status
 */
export const getBillingBalance = async (): Promise<BillingBalance> => {
  const response = await axiosInstance.get<BillingBalance>('/billing/balance/detailed');
  return response.data;
};

/**
 * Get user's transaction history with optional filters.
 * Converts frontend page/page_size params to backend limit/offset params,
 * and maps backend response format to frontend expected format.
 */
export const getTransactions = async (
  params?: TransactionListRequest
): Promise<TransactionListResponse> => {
  // Convert page/page_size to limit/offset for the backend
  const page = params?.page ?? 1;
  const pageSize = params?.page_size ?? 10;
  const backendParams: Record<string, string | number> = {
    limit: pageSize,
    offset: (page - 1) * pageSize,
  };
  if (params?.transaction_type) {
    backendParams.transaction_type = params.transaction_type;
  }
  if (params?.status) {
    backendParams.status = params.status;
  }
  if (params?.start_date) {
    backendParams.start_date = params.start_date;
  }
  if (params?.end_date) {
    backendParams.end_date = params.end_date;
  }

  const response = await axiosInstance.get('/billing/transactions', {
    params: backendParams,
  });

  // Map backend response format to frontend expected format
  const data = response.data;
  const totalCount = data.total_count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return {
    items: data.transactions ?? [],
    total: totalCount,
    page,
    page_size: pageSize,
    total_pages: totalPages,
  };
};

/**
 * Get a specific transaction by ID
 */
export const getTransactionById = async (transactionId: string): Promise<Transaction> => {
  const response = await axiosInstance.get<Transaction>(`/billing/transactions/${transactionId}`);
  return response.data;
};

/**
 * Add funds to user's account balance
 */
export const addFunds = async (paymentData: PaymentRequest): Promise<PaymentResponse> => {
  const response = await axiosInstance.post<PaymentResponse>('/billing/add-funds', paymentData);
  return response.data;
};

/**
 * Process a payment for a service
 */
export const processPayment = async (paymentData: PaymentRequest): Promise<PaymentResponse> => {
  const response = await axiosInstance.post<PaymentResponse>('/billing/payment', paymentData);
  return response.data;
};

/**
 * Get payment methods for the user
 */
export const getPaymentMethods = async (): Promise<PaymentMethod[]> => {
  const response = await axiosInstance.get<PaymentMethod[]>('/billing/payment-methods');
  return response.data;
};

/**
 * Add a new payment method
 */
export const addPaymentMethod = async (
  paymentMethodId: string
): Promise<PaymentMethod> => {
  const response = await axiosInstance.post<PaymentMethod>('/billing/payment-methods', {
    payment_method_id: paymentMethodId,
  });
  return response.data;
};

/**
 * Remove a payment method
 */
export const removePaymentMethod = async (paymentMethodId: string): Promise<void> => {
  await axiosInstance.delete(`/billing/payment-methods/${paymentMethodId}`);
};

/**
 * Set default payment method
 */
export const setDefaultPaymentMethod = async (paymentMethodId: string): Promise<void> => {
  await axiosInstance.put(`/billing/payment-methods/${paymentMethodId}/default`);
};

/**
 * Get billing summary for a date range
 */
export const getBillingSummary = async (
  startDate: string,
  endDate: string
): Promise<BillingSummary> => {
  const response = await axiosInstance.get<BillingSummary>('/billing/summary', {
    params: {
      start_date: startDate,
      end_date: endDate,
    },
  });
  return response.data;
};

/**
 * Export transactions as CSV
 */
export const exportTransactionsCSV = async (params?: TransactionListRequest): Promise<Blob> => {
  const response = await axiosInstance.get('/billing/transactions/export/csv', {
    params,
    responseType: 'blob',
  });
  return response.data;
};

/**
 * Request a refund for a transaction
 */
export const requestRefund = async (
  transactionId: string,
  reason: string
): Promise<Transaction> => {
  const response = await axiosInstance.post<Transaction>(
    `/billing/transactions/${transactionId}/refund`,
    { reason }
  );
  return response.data;
};

/**
 * Get available credit packages (no auth required)
 */
export const getCreditPackages = async (): Promise<CreditPackage[]> => {
  const response = await axiosInstance.get<CreditPackagesListResponse>('/billing/credit-packages');
  return response.data.packages;
};

/**
 * Purchase a credit package
 */
export const purchaseCredits = async (
  data: PurchaseCreditRequest
): Promise<PurchaseCreditResponse> => {
  const response = await axiosInstance.post<PurchaseCreditResponse>(
    '/billing/purchase-credits',
    data
  );
  return response.data;
};

/**
 * Get auto-replenish settings (Phase 2)
 */
export const getAutoReplenishSettings = async (): Promise<AutoReplenishSettings> => {
  const response = await axiosInstance.get<AutoReplenishSettings>('/billing/auto-replenish');
  return response.data;
};

/**
 * Update auto-replenish settings (Phase 2)
 */
export const updateAutoReplenishSettings = async (
  data: AutoReplenishUpdateRequest
): Promise<AutoReplenishSettings> => {
  const response = await axiosInstance.put<AutoReplenishSettings>(
    '/billing/auto-replenish',
    data
  );
  return response.data;
};

// Additional type definitions for billing
export interface PaymentMethod {
  id: string;
  type: 'card' | 'bank_account';
  last4: string;
  brand?: string;
  exp_month?: number;
  exp_year?: number;
  is_default: boolean;
  created_at: string;
}

export interface BillingSummary {
  total_spent: number;
  total_searches: number;
  total_calculations: number;
  period_start: string;
  period_end: string;
  transactions_by_type: Record<string, number>;
}
